<script setup lang="ts">
import { ref } from "vue";
import { AxiosError } from "axios";
import { appContainer } from "../../../container";
import { ErrorResponse } from "../../../interface/ErrorInterface";
import { LoadingService } from "../../../services/loadingService";
import { CompanyService } from "../../../services/manageCompanyService";
import { ToastService } from "../../../services/toastService";
import ValidationErrorComponent from "../../../components/ValidationErrorComponent.vue";
import { QuestionInputTypes } from "../../../interface/QuestionInterface";
const props = defineProps<{
  companyId: number;
}>();

const questionData = ref({
  input_type: null,
  question_text: "",
});

const errors = ref({
  input_type: "",
  question_text: "",
});

const emit = defineEmits(["close", "add"]);

const validationErrors = ref<ErrorResponse>();
const loadingService = appContainer.resolve(LoadingService);
const toastService = appContainer.resolve(ToastService);
const companyService = appContainer.resolve(CompanyService);

function validateForm() {
  let isValid = true;

  // Reset errors
  errors.value = {
    question_text: "",
    input_type: "",
  };

  // Validate question text
  if (!questionData.value.question_text) {
    errors.value.question_text = "Question is required.";
    isValid = false;
  }

  // Validate input type
  if (!questionData.value.input_type) {
    errors.value.input_type = "Please select a question type.";
    isValid = false;
  }

  return isValid;
}

async function addQuestion() {
  if (validateForm()) {
    const loader = await loadingService.show();
    companyService
      .addCompanyQuestion(props.companyId, questionData.value)
      .then(() => {
        toastService.success("Question Added Successfully");
        emit("add");
      })
      .catch((err) => {
        const error = err as AxiosError;
        const errorResponse = error.response?.data as ErrorResponse;
        validationErrors.value = errorResponse;
      })
      .finally(() => loader.hide());
  }
}
</script>

<template>
  <div class="modal-backdrop">
    <div class="modal fade show" id="add-modal">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <p class="modal-title fw-bold">Add Question</p>
            <button
              type="button"
              class="btn-close"
              @click="emit('close')"
            ></button>
          </div>
          <ValidationErrorComponent
            v-if="validationErrors"
            :validations="validationErrors"
          ></ValidationErrorComponent>
          <form @submit.prevent="addQuestion" class="w-100 overflow-y-auto">
            <div class="modal-body">
              <div class="mb-3">
                <label for="companyQuestionType" class="form-label fw-bold"
                  >Type</label
                >
                <select
                  id="companyQuestionType"
                  v-model="questionData.input_type"
                  class="form-select text-capitalize"
                  data-cy=""
                >
                  <option :value="null">Please Select</option>
                  <option
                    v-for="questionType in QuestionInputTypes"
                    :key="questionType"
                    :value="questionType"
                  >
                    {{ questionType }}
                  </option>
                </select>
                <div v-if="errors.input_type" class="error mt-2 text-danger">
                  {{ errors.input_type }}
                </div>
              </div>

              <div class="mb-3">
                <label for="companyQuestionTextAdd" class="form-label fw-bold"
                  >Question</label
                >
                <textarea
                  id="companyQuestionTextAdd"
                  v-model="questionData.question_text"
                  class="form-control"
                  rows="5"
                  data-cy=""
                  maxlength="250"
                ></textarea>
                <div v-if="errors.question_text" class="error mt-2 text-danger">
                  {{ errors.question_text }}
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button
                type="submit"
                class="btn btn-primary btn-sm mx-auto text-white"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.error {
  font-size: 14px;
}
</style>
